import { Button, Stack, Typography, useTheme } from "@mui/material";
import { Ellipsis, FolderClosed, SquarePen } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFolderEditMutation } from "web_myplann/Api/Document/useFolderEditMutation";
import type { DocumentTagResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { clamp2 } from "web_myplann/Portfolio/Components/Document/DocumentItem";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { MyPlannInput } from "web_myplann/Shared/Components/MyPlannInput";

type Props = {
	item: DocumentTagResponseDTO;
};

export const FolderItem = ({ item }: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const { mutate: renameFolder } = useFolderEditMutation();

	const [renameValue, setRenameValue] = useState(item.title);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);

	const handleOpenFolder = () => {
		navigate(Routing.Portfolio.FolderDetail.path(item.title));
	};

	const handleRename = () => {
		renameFolder({
			requestContract: {
				titleToEdit: item.title,
				title: renameValue,
			},
		});
		setIsRenameDialogOpen(false);
		setIsDrawerOpen(false);
	};

	return (
		<Stack key={item.title} onClick={handleOpenFolder}>
			<Stack direction="row" alignItems="center" spacing={2} py={2} px={1.6}>
				<Stack>
					<FolderClosed color={theme.palette.text.primary} size={24} />
				</Stack>
				<Typography
					fontSize={15}
					fontWeight={500}
					sx={{
						...clamp2,
						flex: 1,
					}}
				>
					{item.title}
				</Typography>
				<Ellipsis
					size={16}
					color={theme.palette.grey[500]}
					onClick={(e) => {
						e.stopPropagation();
						setIsDrawerOpen(true);
					}}
				/>
			</Stack>

			<MyPlannDrawer
				open={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				title={t(Resources.Portfolio.List.FolderActionsDrawer.title)}
			>
				<Stack
					px={2}
					pb={2}
					gap={2}
					alignItems="center"
					direction="row"
					onClick={() => setIsRenameDialogOpen(true)}
				>
					<SquarePen color={theme.palette.primary.main} />
					<Typography>
						{t(Resources.Portfolio.List.DocumentActionsDrawer.Actions.rename)}
					</Typography>
				</Stack>
			</MyPlannDrawer>
			<MyPlannDrawer
				open={isRenameDialogOpen}
				onClose={() => {
					setIsRenameDialogOpen(false);
					setIsDrawerOpen(false);
					setRenameValue(item.title);
				}}
				title={t(Resources.Portfolio.List.FolderActionsDrawer.Actions.rename)}
			>
				<Stack px={2} pb={2} gap={1.2}>
					<MyPlannInput
						label={t(
							Resources.Portfolio.List.DocumentActionsDrawer.Rename
								.folderRename,
						)}
						value={renameValue}
						onChange={(e) => setRenameValue(e.target.value)}
					/>
					<Button onClick={handleRename}>{t(Resources.Common.save)}</Button>
				</Stack>
			</MyPlannDrawer>
		</Stack>
	);
};
